import { IconographyTypes } from '@genial/icons';
import { CustomerEntity } from '@genial/services';
import {
  BasketAdvisorOrderEntity,
  BasketCustomerOrderEntity,
  CreateOfferOrderRequest,
  ErrorResponse,
  MetaResponse,
  RequestedOfferDetailRequest,
  UpdateBasketAdvisorOfferRequest,
  UpdateBasketCustomerOfferRequest,
} from '@genial/services/dist/models';

import { AssetSelectedTypeMap } from '../../../store/product/types';

export type CustomerItemTagObject = {
  tagId: string;
  tagName: string;
  enabled?: boolean;
};

export type Tags = {
  list: CustomerItemTagObject[];
  added: { [keyof: number]: CustomerItemTagObject[] };
  removed: { [keyof: number]: CustomerItemTagObject[] };
};

export type TagItemProp = { customerId: number } & CustomerItemTagObject;

export type CartSectionStatus = 'start' | 'complete' | 'locked';

export interface CustomerItemDataProps extends Omit<CustomerEntity, 'tags'> {
  tags: CustomerItemTagObject[];
  hashtags: CustomerItemTagObject[];
  availableBalanceFormatted: string;
  basketCustomerOrderId: string;
}

export type orderByProps = { value: string; direction: 'asc' | 'desc' };

export type CustomerItemDataResponseProps = {
  data: CustomerItemDataProps[];
  meta?: MetaResponse;
};

export enum AssetsMarketTypeEnum {
  FixedIncome = 'FixedIncome',
  Coe = 'COE',
  Fund = 'Fund',
  Equity = 'Equity',
  Treasury = 'Treasury',
}

export type AssetWithErrorResponse = { errors?: ErrorResponse[] };

export type AddedProductItemList = AssetWithErrorResponse &
  AssetSelectedTypeMap['fixedIncome'] &
  AssetSelectedTypeMap['funds'] &
  AssetSelectedTypeMap['equity'] &
  AssetSelectedTypeMap['coes'] &
  AssetSelectedTypeMap['directTreasury'];

export type AddedProductsTypes = 'fixedIncome' | 'funds' | 'coes' | 'equity' | 'directTreasury';

export enum AddedProductsTypesEnum {
  FIXED_INCOME = 'fixedIncome',
  FUNDS = 'funds',
  COES = 'coes',
  EQUITY = 'equity',
  DIRECT_TREASURY = 'directTreasury',
}

export type AddedProductItemData = {
  type: AddedProductsTypes;
  percentage: number;
  assetCount: number;
  total: number;
  totalFormatted: string;
  items: AddedProductItemList[];
};

export type AddedProductItemRouteParamTypeProps = 'cart' | 'totalSummary' | 'requiredAttention';

export interface AddedProductItemSectionProps {
  itemData: AddedProductItemData;
  hasPercentage?: boolean;
  navigationType?: AddedProductItemRouteParamTypeProps;
}

export type appliedAmountFormData = { appliedAmount: string };

export interface CoeVariationStyle {
  name: IconographyTypes.Option;
  color: string;
}

export type CoeVariationStyleValueProp = 1 | 2 | 3 | 'error';

export type totalBasketDeliveredData = {
  totalBasketDeliveredAmount: number;
  totalBasketDeliveredOffers: number;
  totalBasketDeliveredCustomers: number;
};

export type BasketAdvisorListDataItem = {
  totalRequestedAmountFormatted: string;
  applicationType: string;
  createDateFormatted: string;
  updateDateFormatted: string;
  requestUserFullName?: string | null;
} & BasketAdvisorOrderEntity &
  totalBasketDeliveredData;

export type BasketAdvisorListResponseProps = {
  data: BasketAdvisorListDataItem[];
  meta?: MetaResponse;
};

export type PostCustomerBasketServiceProps = {
  basketAdvisorOrderId: string | null;
  customerItem: CustomerItemDataProps;
};

export type PatchCustomerTagService = {
  customerId: number;
  tagCustomerItem: CustomerItemTagObject;
};

export type PostProductBasketServiceProps = Omit<
  CreateOfferOrderRequest,
  'orderType' | 'quantity' | 'requestedPrice' | 'requestedQuantity'
> & {
  quantity?: number;
  requestedPrice?: number;
  requestedQuantity?: number;
};

export type PutProductBasketServiceProps = Omit<
  UpdateBasketAdvisorOfferRequest,
  'orderType' | 'quantity' | 'requestedPrice' | 'requestedQuantity'
> & {
  quantity?: number;
  requestedPrice?: number;
  requestedQuantity?: number;
  requestedOfferDetail?: Partial<RequestedOfferDetailRequest>;
};

export type PutCustomerOfferByBasketServiceProps = Omit<
  UpdateBasketCustomerOfferRequest,
  'orderType' | 'quantity' | 'requestedPrice' | 'requestedQuantity'
> & {
  quantity?: number;
  requestedPrice?: number;
  requestedQuantity?: number;
};

export type BasketCustomerOrderItemResume = {
  data: {
    all: BasketCustomerOrderEntity[];
    errors: BasketCustomerOrderEntity[];
    valid: BasketCustomerOrderEntity[];
  };
  summary: BasketAdvisorListDataItem;
};

export type BasketCustomerOrderItemResumeData = {
  data: BasketCustomerOrderItemResume['data']['all'];
  summary: BasketAdvisorListDataItem;
};

export type RemoveCustomerOfferBasketParams = {
  basketCustomerOfferId: string;
};

export type UpdateCustomerOfferBasketParams = {
  basketCustomerOfferId: string;
  amount?: number;
  requestedAmount?: number;
  price?: number;
  requestedPrice?: number;
  quantity?: number;
  requestedQuantity?: number;
};

export type UpdateProductCartOfferParams = {
  basketOfferId: string;
  amount: number;
  price: number;
  requestedAmount: number;
  quantity: number;
  requestedPrice: number;
  requestedQuantity: number;
  requestedOfferDetail?: Partial<RequestedOfferDetailRequest>;
};
